import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtPKR, fmtDate, fmtDT } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, Search, Eye, CheckCircle, XCircle, Building2 } from 'lucide-react';

// ── Corporate Accounts ────────────────────────────────────────────────────────
export function CorporatePage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [form, setForm] = useState({ name:'', companyName:'', email:'', mobile:'', taxId:'', creditLimit:'0', creditDays:'30', password:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/corporate?search=${encodeURIComponent(search)}`).then(r=>r.data);
      if (r.success) setAccounts(r.data);
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t=setTimeout(load,300); return()=>clearTimeout(t); }, [load]);

  const openDetail = async (acc) => {
    setSelected(acc);
    const r = await api.get(`/corporate/${acc.customer_id}`).then(r=>r.data);
    if (r.success) setDetail(r.data);
  };

  const handleCreate = async () => {
    if (!form.name||!form.email) { toast.error('Name and email required'); return; }
    try {
      const r = await api.post('/corporate', form).then(r=>r.data);
      if (r.success) { toast.success('Corporate account created'); setShowCreate(false); load(); }
      else throw new Error(r.message);
    } catch(e) { toast.error(e.message||'Failed'); }
  };

  return (
    <AppShell title="Corporate Accounts" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}><Plus size={14}/> Add Account</button>
    }>
      <div className="filters-bar">
        <div className="search-input"><Search size={14}/><input placeholder="Search company, email…" value={search} onChange={e=>setSearch(e.target.value)}/></div>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : accounts.length ? (
            <table>
              <thead><tr><th>Company</th><th>Contact</th><th>Shipments</th><th>Total Spend</th><th>Credit Limit</th><th>Used</th><th>KYC</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {accounts.map(a=>(
                  <tr key={a.customer_id}>
                    <td>
                      <div style={{ fontWeight:600 }}>{a.company_name||a.name}</div>
                      <div className="text-sm text-muted">{a.tax_id ? `TIN: ${a.tax_id}` : ''}</div>
                    </td>
                    <td>
                      <div className="text-sm">{a.name}</div>
                      <div className="text-sm text-muted">{a.email}</div>
                    </td>
                    <td>{a.total_shipments||0}</td>
                    <td>{fmtPKR(a.total_spend)}</td>
                    <td>{fmtPKR(a.credit_limit)}</td>
                    <td>
                      {a.credit_limit > 0 && (
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <div style={{ flex:1, height:6, background:'var(--gray-200)', borderRadius:3, overflow:'hidden' }}>
                            <div style={{ height:'100%', background: parseFloat(a.credit_used)/parseFloat(a.credit_limit) > 0.8 ? 'var(--danger)' : 'var(--success)',
                              width:`${Math.min(100,(parseFloat(a.credit_used||0)/parseFloat(a.credit_limit))*100).toFixed(0)}%` }}/>
                          </div>
                          <span style={{ fontSize:'.72rem' }}>{((parseFloat(a.credit_used||0)/parseFloat(a.credit_limit))*100).toFixed(0)}%</span>
                        </div>
                      )}
                    </td>
                    <td><span className={`badge ${a.kyc_status==='VERIFIED'?'badge-success':a.kyc_status==='REJECTED'?'badge-danger':'badge-warning'}`}>{a.kyc_status}</span></td>
                    <td><span className={`badge ${a.is_active?'badge-success':'badge-gray'}`}>{a.is_active?'Active':'Inactive'}</span></td>
                    <td><button className="btn btn-outline btn-sm" onClick={()=>openDetail(a)}><Eye size={12}/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="🏢" title="No corporate accounts" />}
        </div>
      </div>

      {/* Detail Modal */}
      <Modal open={!!selected} onClose={()=>{setSelected(null);setDetail(null);}} title={`Account: ${selected?.company_name||selected?.name}`} size="modal-lg">
        {detail ? (
          <div className="modal-body">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20,
              padding:14, background:'var(--gray-50)', borderRadius:8 }}>
              <InfoItem label="Company"       v={detail.account.company_name||'—'} />
              <InfoItem label="Contact"       v={`${detail.account.name} | ${detail.account.mobile||'—'}`} />
              <InfoItem label="Email"         v={detail.account.email} />
              <InfoItem label="Tax ID"        v={detail.account.tax_id||'—'} />
              <InfoItem label="Credit Limit"  v={fmtPKR(detail.account.credit_limit)} />
              <InfoItem label="Credit Used"   v={fmtPKR(detail.account.credit_used)} />
              <InfoItem label="Total Shipments" v={detail.stats?.total||0} />
              <InfoItem label="Total Spend"   v={fmtPKR(detail.stats?.spend)} />
            </div>
            {detail.contracts.length > 0 && (
              <>
                <div style={{ fontWeight:600, marginBottom:8 }}>Rate Contracts</div>
                {detail.contracts.map(c=>(
                  <div key={c.contract_id} style={{ padding:'8px 12px', background:'var(--gray-50)', borderRadius:6, marginBottom:6, fontSize:'.85rem' }}>
                    <div className="flex-between">
                      <span style={{ fontWeight:500 }}>{c.name||'Contract'}</span>
                      <span className={`badge ${c.status==='ACTIVE'?'badge-success':'badge-gray'}`}>{c.status}</span>
                    </div>
                    <div style={{ color:'var(--gray-500)', fontSize:'.78rem', marginTop:2 }}>
                      Discount: {c.discount_pct}% | Valid: {fmtDate(c.effective_from)} – {c.effective_to ? fmtDate(c.effective_to) : 'Ongoing'}
                    </div>
                  </div>
                ))}
              </>
            )}
            {detail.recentInvoices.length > 0 && (
              <>
                <div style={{ fontWeight:600, margin:'12px 0 8px' }}>Recent Invoices</div>
                {detail.recentInvoices.slice(0,5).map(inv=>(
                  <div key={inv.invoice_id} className="flex-between" style={{ padding:'6px 0', borderBottom:'1px solid var(--gray-100)', fontSize:'.85rem' }}>
                    <code style={{ color:'var(--navy)' }}>{inv.invoice_no}</code>
                    <span>{fmtPKR(inv.total)}</span>
                    <span className={`badge ${inv.payment_status==='PAID'?'badge-success':inv.payment_status==='OVERDUE'?'badge-danger':'badge-warning'}`}>{inv.payment_status}</span>
                    <span className="text-muted text-sm">{fmtDate(inv.created_at)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        ) : <div className="loading-center" style={{ height:200 }}><div className="spinner"/></div>}
      </Modal>

      {/* Create Modal */}
      <Modal open={showCreate} onClose={()=>setShowCreate(false)} title="Add Corporate Account" size="modal-lg">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Contact Name <span className="req">*</span></label><input className="form-control" value={form.name} onChange={set('name')}/></div>
            <div className="form-group"><label className="form-label">Company Name</label><input className="form-control" value={form.companyName} onChange={set('companyName')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Email <span className="req">*</span></label><input className="form-control" type="email" value={form.email} onChange={set('email')}/></div>
            <div className="form-group"><label className="form-label">Mobile</label><input className="form-control" value={form.mobile} onChange={set('mobile')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Tax ID / NTN</label><input className="form-control" value={form.taxId} onChange={set('taxId')}/></div>
            <div className="form-group"><label className="form-label">Credit Limit (PKR)</label><input className="form-control" type="number" value={form.creditLimit} onChange={set('creditLimit')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Credit Days</label><input className="form-control" type="number" value={form.creditDays} onChange={set('creditDays')}/></div>
            <div className="form-group"><label className="form-label">Portal Password</label><input className="form-control" type="password" placeholder="Default: ECL@Corp123" value={form.password} onChange={set('password')}/></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowCreate(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate}><Building2 size={14}/> Create Account</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── Claims ────────────────────────────────────────────────────────────────────
export function ClaimsPage() {
  const [claims, setClaims]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus]   = useState('');
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ customerId:'', trackingId:'', category:'DAMAGED', declaredValue:'0', claimAmount:'0', description:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/claims${status?`?status=${status}`:''}`).then(r=>r.data);
      if (r.success) setClaims(r.data);
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const updateClaim = async (claimId, updates) => {
    try {
      await api.patch(`/claims/${claimId}`, updates).then(r=>r.data);
      toast.success('Claim updated'); load(); setSelected(null);
    } catch { toast.error('Failed'); }
  };

  const handleCreate = async () => {
    if (!form.customerId||!form.description) { toast.error('Customer ID and description required'); return; }
    try {
      const r = await api.post('/claims', form).then(r=>r.data);
      if (r.success) { toast.success(`Claim ${r.data.claim_no} created`); setShowCreate(false); load(); }
    } catch(e) { toast.error(e.message||'Failed'); }
  };

  const STATUS_COLORS = { OPEN:'badge-danger', INVESTIGATING:'badge-warning', APPROVED:'badge-info', REJECTED:'badge-gray', SETTLED:'badge-success' };

  return (
    <AppShell title="Claims Processing" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}><Plus size={14}/> New Claim</button>
    }>
      <div className="filters-bar">
        <select className="form-control" style={{ width:160 }} value={status} onChange={e=>setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {['OPEN','INVESTIGATING','APPROVED','REJECTED','SETTLED'].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : claims.length ? (
            <table>
              <thead><tr><th>Claim No</th><th>Customer</th><th>Tracking</th><th>Category</th><th>Claimed</th><th>Settlement</th><th>Status</th><th>Created</th><th></th></tr></thead>
              <tbody>
                {claims.map(c=>(
                  <tr key={c.claim_id}>
                    <td><code style={{ fontSize:'.78rem', fontWeight:700 }}>{c.claim_no}</code></td>
                    <td>
                      <div style={{ fontWeight:500 }}>{c.customer_name}</div>
                      <div className="text-sm text-muted">{c.customer_mobile}</div>
                    </td>
                    <td><code style={{ fontSize:'.75rem' }}>{c.tracking_id||'—'}</code></td>
                    <td><span className="badge badge-navy">{c.category}</span></td>
                    <td>{fmtPKR(c.claim_amount)}</td>
                    <td>{c.settlement_amount ? fmtPKR(c.settlement_amount) : '—'}</td>
                    <td><span className={`badge ${STATUS_COLORS[c.status]||'badge-gray'}`}>{c.status}</span></td>
                    <td className="text-sm">{fmtDate(c.created_at)}</td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={()=>setSelected(c)}><Eye size={12}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📋" title="No claims found" />}
        </div>
      </div>

      {/* Detail/Action Modal */}
      <Modal open={!!selected} onClose={()=>setSelected(null)} title={`Claim: ${selected?.claim_no}`}>
        {selected && (
          <>
            <div className="modal-body">
              <div style={{ display:'grid', gap:8, padding:12, background:'var(--gray-50)', borderRadius:8, marginBottom:16 }}>
                <InfoItem label="Customer"     v={selected.customer_name} />
                <InfoItem label="Tracking"     v={selected.tracking_id||'—'} />
                <InfoItem label="Category"     v={selected.category} />
                <InfoItem label="Claimed"      v={fmtPKR(selected.claim_amount)} />
                <InfoItem label="Description"  v={selected.description} />
              </div>
              {selected.status === 'OPEN' && (
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-amber" onClick={()=>updateClaim(selected.claim_id,{status:'INVESTIGATING'})}>🔍 Investigate</button>
                  <button className="btn btn-success btn-sm" onClick={()=>{ const amt=prompt('Settlement amount (PKR):'); if(amt) updateClaim(selected.claim_id,{status:'SETTLED',settlementAmount:amt}); }}>✓ Settle</button>
                  <button className="btn btn-danger btn-sm" onClick={()=>updateClaim(selected.claim_id,{status:'REJECTED',resolution:'Claim rejected after review'})}>✗ Reject</button>
                </div>
              )}
              {selected.status === 'INVESTIGATING' && (
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-success" onClick={()=>{ const amt=prompt('Settlement amount (PKR):'); if(amt) updateClaim(selected.claim_id,{status:'SETTLED',settlementAmount:amt}); }}>✓ Approve & Settle</button>
                  <button className="btn btn-danger btn-sm" onClick={()=>updateClaim(selected.claim_id,{status:'REJECTED'})}>✗ Reject</button>
                </div>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* Create Modal */}
      <Modal open={showCreate} onClose={()=>setShowCreate(false)} title="File New Claim">
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Customer ID <span className="req">*</span></label><input className="form-control" value={form.customerId} onChange={set('customerId')}/></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Tracking ID</label><input className="form-control" value={form.trackingId} onChange={set('trackingId')}/></div>
            <div className="form-group"><label className="form-label">Category <span className="req">*</span></label>
              <select className="form-control" value={form.category} onChange={set('category')}>
                {['LOST','DAMAGED','DELAY','WRONG_DELIVERY','OTHER'].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Declared Value (PKR)</label><input className="form-control" type="number" value={form.declaredValue} onChange={set('declaredValue')}/></div>
            <div className="form-group"><label className="form-label">Claim Amount (PKR) <span className="req">*</span></label><input className="form-control" type="number" value={form.claimAmount} onChange={set('claimAmount')}/></div>
          </div>
          <div className="form-group"><label className="form-label">Description <span className="req">*</span></label><textarea className="form-control" rows={3} value={form.description} onChange={set('description')}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowCreate(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate}>File Claim</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── COD Remittance ────────────────────────────────────────────────────────────
export function CodRemittancePage() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate)   params.append('toDate',   toDate);
      const r = await api.get(`/cod/remittance?${params}`).then(r=>r.data);
      if (r.success) setData(r.data);
    } finally { setLoading(false); }
  }, [fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const totalRemittable = data.reduce((a,d)=>a+d.netRemittable,0);

  return (
    <AppShell title="COD Remittance">
      <div className="filters-bar">
        <input className="form-control" type="date" style={{ width:150 }} value={fromDate} onChange={e=>setFromDate(e.target.value)} placeholder="From"/>
        <input className="form-control" type="date" style={{ width:150 }} value={toDate}   onChange={e=>setToDate(e.target.value)}   placeholder="To"/>
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
      </div>

      {data.length > 0 && (
        <div className="alert alert-info" style={{ marginBottom:16 }}>
          Total to remit: <strong>{fmtPKR(totalRemittable)}</strong> across {data.length} customers
        </div>
      )}

      <div style={{ display:'grid', gap:12 }}>
        {loading ? <Spinner /> : data.length ? data.map(d=>(
          <div key={d.customerId} className="card">
            <div className="card-header">
              <div>
                <span className="card-title">{d.companyName||d.customerName}</span>
                {d.companyName && <span className="text-sm text-muted" style={{ marginLeft:8 }}>{d.customerName}</span>}
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontWeight:700, fontSize:'1.1rem', color:'var(--navy)' }}>{fmtPKR(d.netRemittable)}</div>
                <div className="text-sm text-muted">{d.items.length} transactions</div>
              </div>
            </div>
            <div className="card-body">
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:12 }}>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:'.72rem', color:'var(--gray-400)', textTransform:'uppercase' }}>Collected</div>
                  <div style={{ fontWeight:600 }}>{fmtPKR(d.totalCollected)}</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:'.72rem', color:'var(--gray-400)', textTransform:'uppercase' }}>ECL Fees</div>
                  <div style={{ fontWeight:600, color:'var(--danger)' }}>- {fmtPKR(d.totalFees)}</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:'.72rem', color:'var(--gray-400)', textTransform:'uppercase' }}>Net Remittable</div>
                  <div style={{ fontWeight:700, color:'var(--success)' }}>{fmtPKR(d.netRemittable)}</div>
                </div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => {
                const ref = prompt('Bank transfer reference (NEFT/RTGS):');
                if (ref) toast.success(`Remittance recorded: ${ref}`);
              }}>
                💳 Process Remittance
              </button>
            </div>
          </div>
        )) : <EmptyState icon="💵" title="No COD remittances pending" message="All COD collections are either pending deposit or already remitted." />}
      </div>
    </AppShell>
  );
}

// ── Approval Workflows ────────────────────────────────────────────────────────
export function ApprovalsPage() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/approvals?status=${statusFilter}`).then(r=>r.data);
      if (r.success) setApprovals(r.data);
    } catch {} finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const process = async (workflowId, action) => {
    const comments = action==='REJECT' ? prompt('Reason for rejection:') : '';
    try {
      await api.patch(`/approvals/${workflowId}`, { action, comments }).then(r=>r.data);
      toast.success(`Request ${action.toLowerCase()}d`); load();
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const STATUS_COLORS = { PENDING:'badge-warning', APPROVED:'badge-success', REJECTED:'badge-danger', CANCELLED:'badge-gray' };

  return (
    <AppShell title="Approval Workflows">
      <div className="filters-bar">
        <select className="form-control" style={{ width:160 }} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
          {['PENDING','APPROVED','REJECTED','CANCELLED'].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : approvals.length ? (
            <table>
              <thead><tr><th>Type</th><th>Description</th><th>Amount</th><th>Requested By</th><th>Approver Role</th><th>Created</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {approvals.map(a=>(
                  <tr key={a.workflow_id}>
                    <td><span className="badge badge-navy" style={{ fontSize:'.7rem' }}>{a.approval_type}</span></td>
                    <td style={{ maxWidth:200 }}><span className="truncate" style={{ display:'block', fontSize:'.85rem' }}>{a.description}</span></td>
                    <td style={{ fontWeight:500 }}>{a.amount > 0 ? fmtPKR(a.amount) : '—'}</td>
                    <td>
                      <div style={{ fontWeight:500 }}>{a.requester_name}</div>
                      <div className="text-sm text-muted">{a.requester_designation}</div>
                    </td>
                    <td><span className="badge badge-info">{a.current_approver_role}</span></td>
                    <td className="text-sm">{fmtDT(a.created_at)}</td>
                    <td><span className={`badge ${STATUS_COLORS[a.status]||'badge-gray'}`}>{a.status}</span></td>
                    <td>
                      {a.status==='PENDING' && (
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-success btn-sm" onClick={()=>process(a.workflow_id,'APPROVE')}><CheckCircle size={12}/></button>
                          <button className="btn btn-danger btn-sm"  onClick={()=>process(a.workflow_id,'REJECT')}><XCircle size={12}/></button>
                        </div>
                      )}
                      {a.status!=='PENDING' && <span className="text-sm text-muted">{a.comments||'—'}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="✅" title={statusFilter==='PENDING' ? 'No pending approvals' : 'No approvals found'} />}
        </div>
      </div>
    </AppShell>
  );
}

// ── MIS / DOR Report ──────────────────────────────────────────────────────────
export function MISReportPage() {
  const [dor, setDor]           = useState(null);
  const [weekly, setWeekly]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [date, setDate]         = useState(new Date().toISOString().slice(0,10));
  const [tab, setTab]           = useState('dor');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d,w] = await Promise.all([
        api.get(`/reports/dor?date=${date}`).then(r=>r.data),
        api.get(`/reports/weekly?endDate=${date}`).then(r=>r.data),
      ]);
      if (d.success) setDor(d.data);
      if (w.success) setWeekly(w.data);
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  return (
    <AppShell title="MIS Reports" actions={
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <input className="form-control" type="date" style={{ width:150 }} value={date} onChange={e=>setDate(e.target.value)} />
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
        <button className="btn btn-primary btn-sm" onClick={() => window.print()}>🖨️ Print</button>
      </div>
    }>
      <div className="tabs">
        <div className={`tab${tab==='dor'?' active':''}`} onClick={()=>setTab('dor')}>Daily Operational Report (DOR)</div>
        <div className={`tab${tab==='weekly'?' active':''}`} onClick={()=>setTab('weekly')}>Weekly Summary</div>
      </div>

      {loading ? <Spinner /> : (
        <>
          {tab==='dor' && dor && (
            <div style={{ display:'grid', gap:16 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
                <div className="card">
                  <div className="card-header"><span className="card-title">Shipment Volumes — {dor.date}</span></div>
                  <div className="card-body">
                    {dor.volumes.map(v=>(
                      <div key={v.status} className="flex-between" style={{ padding:'6px 0', borderBottom:'1px solid var(--gray-100)' }}>
                        <span style={{ fontSize:'.85rem' }}>{v.status.replace(/_/g,' ')}</span>
                        <strong>{v.count}</strong>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="card">
                  <div className="card-header"><span className="card-title">COD Collections</span></div>
                  <div className="card-body">
                    <div style={{ textAlign:'center', padding:12 }}>
                      <div style={{ fontSize:'1.6rem', fontWeight:700, color:'var(--navy)' }}>{fmtPKR(dor.cod?.collected)}</div>
                      <div className="text-sm text-muted">from {dor.cod?.count||0} shipments</div>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-header"><span className="card-title">Tickets</span></div>
                  <div className="card-body">
                    {dor.tickets.map(t=>(
                      <div key={t.status} className="flex-between" style={{ padding:'6px 0', borderBottom:'1px solid var(--gray-100)' }}>
                        <span style={{ fontSize:'.85rem' }}>{t.status}</span><strong>{t.count}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-header"><span className="card-title">Exceptions</span></div>
                <div className="card-body">
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, textAlign:'center' }}>
                    <ExBox label="Delayed"  v={dor.exceptions?.delayed}  color="var(--warning)" />
                    <ExBox label="Damaged"  v={dor.exceptions?.damaged}  color="var(--danger)"  />
                    <ExBox label="Lost"     v={dor.exceptions?.lost}     color="var(--danger)"  />
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab==='weekly' && weekly && (
            <div style={{ display:'grid', gap:16 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
                <ExBox label="Total Shipments"    v={weekly.summary?.totalShipments}    color="var(--navy)" />
                <ExBox label="Total Revenue"      v={fmtPKR(weekly.summary?.totalRevenue)} color="var(--success)" />
                <ExBox label="Delivery Success"   v={weekly.summary?.deliverySuccessRate} color="var(--amber)" />
              </div>
              <div className="card">
                <div className="card-header"><span className="card-title">Daily Breakdown — {weekly.startDate} to {weekly.endDate}</span></div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Date</th><th>Total</th><th>Delivered</th><th>RTO</th><th>Revenue</th><th>COD Volume</th></tr></thead>
                    <tbody>
                      {weekly.days.map(d=>(
                        <tr key={d.date}>
                          <td style={{ fontFamily:'monospace', fontSize:'.8rem' }}>{d.date}</td>
                          <td>{d.total_shipments}</td>
                          <td style={{ color:'var(--success)', fontWeight:500 }}>{d.delivered}</td>
                          <td style={{ color:'var(--danger)' }}>{d.rto||0}</td>
                          <td style={{ fontWeight:500 }}>{fmtPKR(d.revenue)}</td>
                          <td>{fmtPKR(d.cod_volume)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </AppShell>
  );
}

function InfoItem({ label, v }) {
  return (
    <div>
      <span style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{label}: </span>
      <span style={{ fontSize:'.85rem', fontWeight:500 }}>{v}</span>
    </div>
  );
}

function ExBox({ label, v, color }) {
  return (
    <div style={{ textAlign:'center', padding:'12px 8px', border:`1.5px solid ${color}30`, borderRadius:8 }}>
      <div style={{ fontSize:'1.4rem', fontWeight:700, color }}>{v||0}</div>
      <div style={{ fontSize:'.75rem', color:'var(--gray-500)', marginTop:4 }}>{label}</div>
    </div>
  );
}

// ── Async report generation + CSV export (MGMT-FE-006, MGMT-API-002) ─────────
export function AsyncReportPage() {
  const [jobId, setJobId]     = useState(null);
  const [job, setJob]         = useState(null);
  const [polling, setPolling] = useState(false);
  const [form, setForm] = useState({ reportType:'DOR', date: new Date().toISOString().slice(0,10) });
  const pollRef = React.useRef(null);

  const startReport = async () => {
    try {
      const r = await api.post('/reports/generate', {
        reportType: form.reportType,
        params: { date: form.date },
      }).then(r=>r.data);
      if (r.success) {
        setJobId(r.data.jobId);
        setJob({ status:'PROCESSING', progress:0 });
        setPolling(true);
        toast.success('Report generation started');
      }
    } catch(e) { toast.error(e.response?.data?.message||'Failed to start report'); }
  };

  useEffect(() => {
    if (!polling || !jobId) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/reports/jobs/${jobId}`).then(r=>r.data);
        if (r.success) {
          setJob(r.data);
          if (['COMPLETED','FAILED'].includes(r.data.status)) {
            setPolling(false);
            clearInterval(pollRef.current);
            if (r.data.status==='COMPLETED') toast.success('Report ready!');
            else toast.error('Report generation failed');
          }
        }
      } catch { setPolling(false); clearInterval(pollRef.current); }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [polling, jobId]);

  const downloadCSV = (result) => {
    if (!result?.volumes) return;
    const rows = [['Status','Count'],...result.volumes.map(v=>[v.status,v.count])];
    const csv = rows.map(r=>r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,'+encodeURIComponent(csv);
    a.download = `${result.type||'report'}-${result.date||'export'}.csv`;
    a.click();
  };

  const REPORT_TYPES = ['DOR','WEEKLY','MONTHLY','COD_SUMMARY','DELIVERY_PERFORMANCE','EXCEPTION_REPORT'];

  return (
    <AppShell title="Report Generation">
      <div style={{ maxWidth:600 }}>
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title">Generate Report</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="rpt-type">Report Type</label>
                <select id="rpt-type" className="form-control" value={form.reportType} onChange={e=>setForm(f=>({...f,reportType:e.target.value}))}>
                  {REPORT_TYPES.map(t=><option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="rpt-date">Date</label>
                <input id="rpt-date" className="form-control" type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} />
              </div>
            </div>
            <button className="btn btn-primary w-full" onClick={startReport} disabled={polling} aria-busy={polling}>
              {polling ? 'Generating…' : '⚡ Generate Report'}
            </button>
          </div>
        </div>

        {job && (
          <div className="card">
            <div className="card-header"><span className="card-title">Job Status</span></div>
            <div className="card-body">
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                <span style={{ fontFamily:'monospace', fontSize:'.78rem', color:'var(--gray-400)' }}>{jobId}</span>
                <span className={`badge ${job.status==='COMPLETED'?'badge-success':job.status==='FAILED'?'badge-danger':'badge-warning'}`}>{job.status}</span>
              </div>
              {job.status==='PROCESSING' && (
                <div style={{ height:8, background:'var(--gray-200)', borderRadius:4, overflow:'hidden' }}>
                  <div style={{ height:'100%', background:'var(--amber)', borderRadius:4, width:`${job.progress||30}%`, transition:'width .5s',
                    animation:'pulse 1.5s ease-in-out infinite' }}/>
                </div>
              )}
              {job.status==='COMPLETED' && job.result && (
                <div>
                  <div className="alert alert-success" style={{ marginBottom:12, fontSize:'.85rem' }}>
                    ✅ Report complete — {job.completedAt ? new Date(job.completedAt).toLocaleTimeString() : ''}
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <button className="btn btn-outline btn-sm" onClick={()=>downloadCSV(job.result)}>⬇ Download CSV</button>
                    <button className="btn btn-outline btn-sm" onClick={()=>window.print()}>🖨️ Print</button>
                  </div>
                  {job.result.volumes && (
                    <div style={{ marginTop:12 }}>
                      {job.result.volumes.map(v=>(
                        <div key={v.status} className="flex-between" style={{ padding:'5px 0', borderBottom:'1px solid var(--gray-100)', fontSize:'.85rem' }}>
                          <span>{v.status?.replace(/_/g,' ')}</span><strong>{v.count}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {job.status==='FAILED' && (
                <div className="alert alert-danger">{job.error||'Report generation failed'}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
